import { FREE_MONTHLY_CREDITS } from "./credits";

export const landingPage = {
  header: {
    pill: "Your AI listing assistant",
    title: "Extra hands for every listing.",
    subtitle1: "Most agents are doing the work of an entire team.",
    subtitle2: "Listino helps you prep in minutes.",
    button: "Get started",
    // Shown under the hero address bar.
    freeSignal: `Free to start — ${FREE_MONTHLY_CREDITS.toLocaleString("en-US")} credits a month, no card required.`,
  },

  anotherSetOfHands: {
    title: "Your pre-listing package, done.",

    cta: "View a sample (PDF)",
    tabs: [
      {
        key: "research",
        label: "Research",
        blurb:
          "Property details, market analysis, comparable listings, buyer insights, and neighborhood context.",
      },
      {
        key: "recommend",
        label: "Strategy",
        blurb:
          "Pricing guidance, staging ideas, photo touch-ups, and property-specific recommendations.",
      },
      {
        key: "present",
        label: "Presentation",
        blurb:
          "Seller-ready materials that bring everything together in one polished, personalized presentation.",
      },
    ],
  },

  listingJourney: {
    stem: "Use Listino when",
    subtitle: "Listino prepares the research, strategy, and seller materials you need next.",
    milestones: [
      {
        key: "initial-consultation",
        label: "Initial consultation",
        phrase: "sellers want to meet now.",
        outputs: [
          "Property research",
          "Neighborhood insights",
          "Quick CMA",
          "Personalized consultation materials",
        ],
        outcome:
          "Walk into any meeting looking like you spent hours preparing, even if you only had ten minutes in the car.",
      },
      {
        key: "listing-presentation",
        label: "Listing presentation",
        phrase: "you need to win the listing.",
        outputs: [
          "Seller presentation",
          "Property positioning",
          "Pricing strategy",
          "Follow-up materials",
        ],
        outcome:
          "Every seller has already done their research. Show them yours with materials that feel as personalized as your advice.",
      },
      {
        key: "home-preparation",
        label: "Home preparation",
        phrase: "a seller needs convincing.",
        outputs: [
          "Staging plan",
          "Decluttering guidance",
          "Seller action plan",
          "Marketing strategy",
        ],
        outcome:
          "Turn pricing, positioning, staging, and repair recommendations into a personalized action plan sellers can see.",
      },
      {
        key: "listing-refresh",
        label: "Listing refresh",
        phrase: "the listing stalls.",
        outputs: [
          "Pricing review",
          "Competitive position",
          "Presentation audit",
          "Refresh strategy",
        ],
        outcome:
          "Use research and property-specific materials to show sellers why the price, presentation, or strategy needs to change.",
      },
      {
        key: "listing-takeover",
        label: "Listing takeover",
        phrase: "you're the second agent.",
        outputs: [
          "Pricing and positioning audit",
          "Photo and presentation review",
          "Listing copy assessment",
          "Priority improvements",
        ],
        outcome:
          "Audit the prior listing strategy so you can explain what you would do differently and where you would start.",
      },
    ],
  },

  howItWorks: {
    title: "Built for the time between appointments.",
    steps: [
      {
        number: "1",
        title: "Enter an address",
        description: "Start with just an address. Add photos whenever you're ready.",
        icon: "address",
      },
      {
        number: "2",
        title: "Listino does the prep",
        description: "Research, strategy, and seller materials, ready for your review in minutes.",
        icon: "spark",
        automatic: true,
      },
      {
        number: "3",
        title: "Personalize & share",
        description: "Review, edit, and share as a PDF, PowerPoint, one-pager, or social content.",
        icon: "share",
      },
    ],
    button: "Get started",
  },

  dataBackedSection: {
    title: "Before you put your name on it...",
    subtitle: "Every Listino output is built to be reviewed, personalized, and shared as your own.",
    benefits: [
      {
        title: "Every section is editable",
        description: "Review, rewrite, remove, or add anything before it reaches a seller.",
      },
      {
        title: "Make it unmistakably yours",
        description: "Add your logo, colors, contact information, and brokerage branding.",
      },
      {
        title: "Personalized for the property",
        description:
          "Built around the address, photos, and information you provide, not a generic template.",
      },
      {
        title: "Grounded in trusted real estate data",
        description:
          "Property records, market context, and neighborhood insights support the work.",
      },
      {
        title: "You make the final call",
        description: "Listino prepares the work. You decide what belongs in front of your client.",
      },
    ],
    button: "Start your listing prep",
  },

  touchups: {
    title: "Upload photos. Get a plan.",

    staging: {
      label: "Staging recommendations",
      title: "Help sellers see the possibilities.",
      description:
        "Room-by-room guidance on what to declutter, restage, and refresh before professional photos. Make a plan you can hand a seller, with the reasoning behind each call.",

      annotations: [
        { label: "Declutter the counter", top: "24%", left: "26%" },
        { label: "Restage the sofa", top: "60%", left: "62%" },
        { label: "Warm up the entry", top: "82%", left: "22%" },
      ],
      cta: "See a staging plan",
      // Falls back to DEMO_STAGING_URL — the photo-assessment excerpt, not the
      // whole 29-page report this used to open.
      sampleUrl: null,
    },

    photoTouchups: {
      label: "Automatic photo editing",
      title: "Your actual photos, cleaned up",
      description:
        "We re-render the listing photos you already have — greener lawns, brighter skies, less clutter — so buyers see the home's potential. Realistic, never fake.",

      samples: [
        {
          caption: "Greened up the front lawn",
          originalUrl:
            "https://listing-iq-assets.s3.amazonaws.com/demo_touchups/front-lawn-before.jpg",
          editedUrl:
            "https://listing-iq-assets.s3.amazonaws.com/demo_touchups/front-lawn-after.jpg",
        },
        {
          caption: "Brightened an overcast exterior",
          originalUrl:
            "https://listing-iq-assets.s3.amazonaws.com/demo_touchups/overcast-exterior-before.jpg",
          editedUrl:
            "https://listing-iq-assets.s3.amazonaws.com/demo_touchups/overcast-exterior-after.jpg",
        },
      ],
      cta: "How photos and video work",
      // No link: /touchups/:uuid is owner-scoped, so this pointed at a real
      // customer's prep and returned 403 for every prospect who clicked it.
      // The before/after pair beside this copy is already interactive — click
      // either frame for the full-size lightbox — so the section loses
      // nothing. Restore a CTA only when a purpose-built sample exists.
      sampleUrl: null,
    },
  },

  footer: {
    title: "Have questions? Get in touch.",
    description:
      "Whether you're preparing your first listing or your hundredth, we're here to help you get the most out of Listino.",
    email: "contact@listino.ai",
  },
};
